import { useState, useEffect, useRef } from "react";
import { uid, T, mono } from "./utils";
import { supabase } from "./supabase";

const REGIONS = ["Europe","North Am","Asia-Caucasus-Africa","Latam","Oceania"];
const REGION_SHORT = {"Europe":"EU","North Am":"NA","Asia-Caucasus-Africa":"ACA","Latam":"LA","Oceania":"OC"};
const REGION_COLOR = {"Europe":"#4a6fa5","North Am":"#c0392b","Asia-Caucasus-Africa":"#c9843a","Latam":"#3a8a7c","Oceania":"#9b7ec8"};
const STATUSES = ["Hedefleniyor","Applied","Selected","Rejected","Contacted","Not Applied","2027","Ada"];
const STATUS_COLOR = {"Hedefleniyor":"#888","Applied":"#4a6fa5","Selected":"#2d7a2d","Rejected":"#c0392b","Contacted":"#c9843a","Not Applied":"#aaa","2027":"#9b7ec8","Ada":"#e17055"};
const STATUS_BG = {"Hedefleniyor":"#f5f5f5","Applied":"#eaf0fa","Selected":"#eafaea","Rejected":"#faeaea","Contacted":"#fdf3e3","Not Applied":"#f0f0f0","2027":"#f0eeff","Ada":"#fef0ec"};
const ALL_TAGS = ["DOCS","QUEER","WOMEN","HUMAN RIGHTS","ANIMATION","SHORT","EXPERIMENTAL"];
const TAG_COLOR = {"DOCS":"#3a8a7c","QUEER":"#9b7ec8","WOMEN":"#e4572e","HUMAN RIGHTS":"#c9843a","ANIMATION":"#4a6fa5","SHORT":"#888","EXPERIMENTAL":"#888"};

const MONTHS_MAP = {
  "jan":1,"feb":2,"mar":3,"apr":4,"may":5,"jun":6,"jul":7,"aug":8,"sep":9,"oct":10,"nov":11,"dec":12,
  "oca":1,"şub":2,"sub":2,"mar":3,"nis":4,"may":5,"haz":6,"tem":7,"ağu":8,"agu":8,"eyl":9,"eki":10,"kas":11,"ara":12,
  "january":1,"february":2,"march":3,"april":4,"june":6,"july":7,"august":8,"september":9,"october":10,"november":11,"december":12,
  "ocak":1,"şubat":2,"mart":3,"nisan":4,"mayıs":5,"haziran":6,"temmuz":7,"ağustos":8,"eylül":9,"ekim":10,"kasım":11,"aralık":12,
};
const MONTH_NAMES_TR = ["","Oca","Sub","Mar","Nis","May","Haz","Tem","Agu","Eyl","Eki","Kas","Ara"];

function parseFestivalDate(festival_dates, deadline) {
  if (festival_dates) {
    const s = festival_dates.trim().toLowerCase().replace(/\(tahmini\)/g,"").replace(/\(bienal\)/g,"").trim();
    // ISO: 2026-11-12
    const iso = s.match(/([0-9]{4})-([0-9]{2})-([0-9]{2})/);
    if (iso) return new Date(iso[0]);
    // DD-DD Mon YYYY or DD Mon - DD Mon YYYY
    const m1 = s.match(/([0-9]{1,2})\s*[-]\s*[0-9]{1,2}\s+([a-zışğüöç]+)\s+([0-9]{4})/);
    if (m1) { const mon = MONTHS_MAP[m1[2].slice(0,3)] || MONTHS_MAP[m1[2]]; if (mon) return new Date(parseInt(m1[3]), mon-1, parseInt(m1[1])); }
    // DD Mon YYYY
    const m2 = s.match(/([0-9]{1,2})\s+([a-zışğüöç]+)\s+([0-9]{4})/);
    if (m2) { const mon = MONTHS_MAP[m2[2].slice(0,3)] || MONTHS_MAP[m2[2]]; if (mon) return new Date(parseInt(m2[3]), mon-1, parseInt(m2[1])); }
    // Mon YYYY (e.g. "Nov 2026", "Kasım 2026")
    const m3 = s.match(/([a-zışğüöç]+)\s+([0-9]{4})/);
    if (m3) { const mon = MONTHS_MAP[m3[1].slice(0,3)] || MONTHS_MAP[m3[1]]; if (mon) return new Date(parseInt(m3[2]), mon-1, 1); }
    // Just month name (e.g. "Kasım", "NOVEMBER", "Ekim-Kasım")
    const m4 = s.match(/^([a-zışğüöç]+)/);
    if (m4) { const mon = MONTHS_MAP[m4[1].slice(0,3)] || MONTHS_MAP[m4[1]]; if (mon) return new Date(2026, mon-1, 1); }
  }
  if (deadline && deadline.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/)) return new Date(deadline);
  return null;
}

function festivalSortKey(m) {
  const d = parseFestivalDate(m.festival_dates, m.deadline);
  if (!d || isNaN(d.getTime())) return "9999-99-99";
  return d.toISOString().slice(0,10);
}

function festivalMonthLabel(m) {
  const d = parseFestivalDate(m.festival_dates, m.deadline);
  if (!d || isNaN(d.getTime())) return "";
  return MONTH_NAMES_TR[d.getMonth()+1] || "";
}

function daysUntil(d) {
  if (!d || !d.match(/^\d{4}-\d{2}-\d{2}$/)) return null;
  const now = new Date(); now.setHours(0,0,0,0);
  return Math.round((new Date(d)-now)/(1000*60*60*24));
}

function NoteInput({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(()=>setVal(value),[value]);
  const commit = () => { setEditing(false); if(val!==value) onSave(val); };
  if (editing) return (
    <input value={val} onChange={e=>setVal(e.target.value)}
      onBlur={commit} onKeyDown={e=>{if(e.key==="Enter")commit();if(e.key==="Escape"){setVal(value);setEditing(false);}}}
      autoFocus placeholder="Not ekle…"
      style={{flex:1,fontSize:11,border:"1px solid #4a6fa5",borderRadius:2,padding:"1px 6px",outline:"none",fontFamily:"'Inter',sans-serif",color:T.ink,minWidth:60,maxWidth:200}}/>
  );
  return (
    <span onDoubleClick={()=>setEditing(true)} title="Not eklemek için çift tıkla"
      style={{flex:1,fontSize:11,color:value?"#c9843a":T.border,cursor:"text",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:200,fontStyle:value?"normal":"italic"}}>
      {value||"not ekle…"}
    </span>
  );
}

function AdaNoteInput({ value, onSave }) {
  const [val, setVal] = useState(value);
  const [focused, setFocused] = useState(false);
  useEffect(()=>setVal(value),[value]);
  const commit = () => { setFocused(false); if(val!==value) onSave(val); };
  return (
    <textarea
      value={val}
      onChange={e=>setVal(e.target.value)}
      onFocus={()=>setFocused(true)}
      onBlur={commit}
      onKeyDown={e=>{ if(e.key==="Escape") { setVal(value); e.target.blur(); } }}
      placeholder="Not ekle..."
      rows={focused||val ? Math.max(2, val.split("\n").length) : 1}
      style={{
        width:"100%", resize:"vertical", border:`1px solid ${focused?"#e17055":T.border}`,
        borderRadius:3, padding:"5px 8px", fontSize:11, fontFamily:"'Inter',sans-serif",
        color:T.ink, background:focused?"#fff":T.bg, outline:"none",
        transition:"border-color 0.15s, background 0.15s",
        boxSizing:"border-box", lineHeight:1.5,
        color: val ? T.ink : T.mute,
      }}
    />
  );
}

function AddProjectRow({ projects, activeProjectIds, onAdd }) {
  const [open, setOpen] = useState(false);
  const available = projects.filter(p => p.phase !== "Değerlendirilecek" && !activeProjectIds.includes(p.id));
  if (available.length === 0) return null;
  return (
    <div>
      <button onClick={() => setOpen(o => !o)} style={{
        ...mono, fontSize: 10, background: "none", border: `1px solid ${T.border}`,
        borderRadius: 3, padding: "7px 14px", cursor: "pointer", color: T.mute,
        display: "flex", alignItems: "center", gap: 6
      }}>
        <span style={{ fontSize: 14, lineHeight: 1 }}>{open ? "−" : "+"}</span> Film Ekle
      </button>
      {open && (
        <div style={{ marginTop: 8, border: `1px solid ${T.border}`, borderRadius: 3, overflow: "hidden", background: "#fff" }}>
          {available.map((p, i) => (
            <button key={p.id} onClick={() => { onAdd(p); setOpen(false); }}
              style={{ display: "block", width: "100%", textAlign: "left", fontSize: 13, color: T.mute, background: "none", border: "none", borderBottom: i < available.length - 1 ? `1px solid ${T.borderLight}` : "none", padding: "9px 14px", cursor: "pointer", fontFamily: "'Inter',sans-serif" }}
              onMouseEnter={e => { e.currentTarget.style.background = T.bg; e.currentTarget.style.color = T.ink; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = T.mute; }}>
              {p.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function FestivalView({ initialProjectId, accessLevel }) {
  const [masterFests, setMasterFests] = useState([]);
  const [filmLinks, setFilmLinks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Active project ids (localStorage)
  const [activeProjectIds, setActiveProjectIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("festival_active_projects") || "[]"); } catch { return []; }
  });
  const [selProjectId, setSelProjectId] = useState(null);

  // Master list filters
  const [search, setSearch] = useState("");
  const [filterRegion, setFilterRegion] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [filterLabel, setFilterLabel] = useState(""); // "turkey" | "applied" | "selected"

  // Editing
  const [editingMaster, setEditingMaster] = useState(null); // festival id
  const [editForm, setEditForm] = useState({});

  // Adding new master festival
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ festival_name:"", country:"", region:"", deadline:"", festival_dates:"", link:"", tags:"", category:"BOTH" });

  // Drag
  const draggingId = useRef(null);
  const draggingLinkId = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [dragOver2027, setDragOver2027] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [filterYear, setFilterYear] = useState("all"); // "all" | "2026" | "2027"
  const [filterFilmRegion, setFilterFilmRegion] = useState(""); // "" | "EU" | "NA" etc.

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    if (initialProjectId) {
      setSelProjectId(initialProjectId);
      setActiveProjectIds(prev => {
        if (prev.includes(initialProjectId)) return prev;
        const next = [...prev, initialProjectId];
        localStorage.setItem("festival_active_projects", JSON.stringify(next));
        return next;
      });
    }
  }, [initialProjectId]);

  async function loadAll() {
    setLoading(true);
    const [{ data: m }, { data: l }, { data: p }] = await Promise.all([
      supabase.from("master_festivals").select("*").order("festival_name"),
      supabase.from("film_festivals").select("*"),
      supabase.from("projects").select("id,title,kind,phase,poster_url").order("title"),
    ]);
    setMasterFests(m || []);
    setFilmLinks(l || []);
    setProjects(p || []);
    if (!selProjectId && initialProjectId) setSelProjectId(initialProjectId);
    setLoading(false);
  }

  async function addToFilm(masterId) {
    if (!selProjectId) return;
    if (filmLinks.find(l => l.master_id === masterId && l.project_id === selProjectId)) return;
    const link = { id: uid(), project_id: selProjectId, master_id: masterId, status: "Hedefleniyor", applied_date: "", film_specific_notes: "" };
    setFilmLinks(prev => [...prev, link]);
    await supabase.from("film_festivals").insert(link);
  }

  async function removeFromFilm(masterId) {
    if (!selProjectId) return;
    setFilmLinks(prev => prev.filter(l => !(l.master_id === masterId && l.project_id === selProjectId)));
    await supabase.from("film_festivals").delete().eq("master_id", masterId).eq("project_id", selProjectId);
  }

  async function updateStatus(linkId, status) {
    setFilmLinks(prev => prev.map(l => l.id === linkId ? { ...l, status } : l));
    await supabase.from("film_festivals").update({ status }).eq("id", linkId);
  }

  async function updateNote(linkId, note) {
    setFilmLinks(prev => prev.map(l => l.id === linkId ? { ...l, film_specific_notes: note } : l));
    await supabase.from("film_festivals").update({ film_specific_notes: note }).eq("id", linkId);
  }

  async function updateNote(linkId, note) {
    setFilmLinks(prev => prev.map(l => l.id === linkId ? { ...l, film_specific_notes: note } : l));
    await supabase.from("film_festivals").update({ film_specific_notes: note }).eq("id", linkId);
  }

  async function updateEmail(linkId, email) {
    setFilmLinks(prev => prev.map(l => l.id === linkId ? { ...l, contact_email: email } : l));
    await supabase.from("film_festivals").update({ contact_email: email }).eq("id", linkId);
  }

  async function updateLabels(linkId, labels) {
    setFilmLinks(prev => prev.map(l => l.id === linkId ? { ...l, labels } : l));
    await supabase.from("film_festivals").update({ labels }).eq("id", linkId);
  }

  async function addFestivalTask(festivalName, note, assignee="ADA") {
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;
    const body = note ? `${festivalName} — ${note}` : festivalName;
    const text = `BAŞVURU (İlk Temas): ${body}`;
    const t = { id: uid(), text, done: false, due_date: dateStr, project_id: selProject?.id||null, etc: !selProject, personal: false, assignee, assignee2: "", notified: true };
    await supabase.from("tasks").insert(t);
    alert(`Görev eklendi → ${assignee}: "${text}"`);
  }

  async function deleteMaster(id) {
    if (!window.confirm("Bu festivali master listeden silmek istediğine emin misin?")) return;
    setMasterFests(prev => prev.filter(m => m.id !== id));
    setFilmLinks(prev => prev.filter(l => l.master_id !== id));
    await supabase.from("master_festivals").delete().eq("id", id);
  }

  async function saveMasterEdit() {
    setMasterFests(prev => prev.map(m => m.id === editingMaster ? { ...m, ...editForm } : m));
    await supabase.from("master_festivals").update(editForm).eq("id", editingMaster);
    setEditingMaster(null);
  }

  async function addMaster() {
    if (!addForm.festival_name.trim()) return;
    const nf = { id: uid(), ...addForm };
    setMasterFests(prev => [...prev, nf].sort((a, b) => a.festival_name.localeCompare(b.festival_name)));
    await supabase.from("master_festivals").insert(nf);
    setAddForm({ festival_name: "", country: "", region: "", deadline: "", festival_dates: "", link: "", tags: "", category: "BOTH" });
    setShowAddForm(false);
  }

  function removeActiveProject(pid) {
    const next = activeProjectIds.filter(id => id !== pid);
    setActiveProjectIds(next);
    localStorage.setItem("festival_active_projects", JSON.stringify(next));
    if (selProjectId === pid) setSelProjectId(next[0] || null);
  }

  // Filtered master list
  const filtered = masterFests.filter(m => {
    if (search && !m.festival_name.toLowerCase().includes(search.toLowerCase()) && !(m.country || "").toLowerCase().includes(search.toLowerCase())) return false;
    if (filterRegion && m.region !== filterRegion) return false;
    if (filterTag && !(m.tags || "").split(",").map(t => t.trim()).includes(filterTag)) return false;
    return true;
  });

  // Film's festival links
  const filmFestIds = selProjectId ? filmLinks.filter(l => l.project_id === selProjectId).map(l => l.master_id) : [];
  const filterLabelFn = (link) => {
    if (!filterLabel) return true;
    const m = masterFests.find(x => x.id === link.master_id);
    if (filterLabel === "turkey") return (m?.country||"").toLowerCase().includes("türk") || (m?.country||"").toLowerCase().includes("turkey");
    if (filterLabel === "applied") return link.status === "Applied";
    if (filterLabel === "selected") return link.status === "Selected";
    return true;
  };
  const filmFestLinks = selProjectId ? filmLinks.filter(l => l.project_id === selProjectId).filter(filterLabelFn).filter(l => {
    if (filterYear === "all") return true;
    const m = masterFests.find(x => x.id === l.master_id);
    if (filterYear === "2027") return l.status === "2027";
    if (filterYear === "2026") {
      if (l.status === "2027") return false;
      const dates = (m?.festival_dates || "") + " " + (m?.deadline || "");
      return dates.includes("2026") || (!dates.includes("2027") && !dates.includes("2025"));
    }
    return true;
  }).filter(l => {
    if (!filterFilmRegion) return true;
    const m = masterFests.find(x => x.id === l.master_id);
    return (m?.region || "") === filterFilmRegion;
  }) : [];

  const validProjects = projects.filter(p => p.phase !== "Değerlendirilecek");
  const projectsWithActivity = validProjects.filter(p => filmLinks.some(l => l.project_id === p.id) && p.poster_url);
  const activeProjects = activeProjectIds.length > 0 ? validProjects.filter(p => activeProjectIds.includes(p.id)) : projectsWithActivity;
  const selProject = projects.find(p => p.id === selProjectId);

  if (loading) return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ ...mono, color: T.mute }}>Loading</span></div>;

  return (
    <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

      {/* ── LEFT: Master List ── */}
      <div style={{ width: 340, borderRight: `1px solid ${T.border}`, display: "flex", flexDirection: "column", background: "#fff", flexShrink: 0 }}>

        {/* Header */}
        <div style={{ padding: "16px 16px 10px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ ...mono, fontSize: 9, color: T.mute }}>MASTER LİSTE · {masterFests.length} festival</div>
            <button onClick={() => setShowAddForm(s => !s)}
              style={{ ...mono, fontSize: 8, background: T.ink, color: "#fff", border: "none", borderRadius: 2, padding: "3px 8px", cursor: "pointer" }}>+ Ekle</button>
          </div>

          {/* Add form */}
          {showAddForm && (
            <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 3, padding: "10px", marginBottom: 8 }}>
              <input value={addForm.festival_name} onChange={e => setAddForm(p => ({ ...p, festival_name: e.target.value }))} placeholder="Festival adı *" autoFocus
                style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: 2, padding: "5px 8px", fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none", marginBottom: 5 }} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5, marginBottom: 5 }}>
                <input value={addForm.country} onChange={e => setAddForm(p => ({ ...p, country: e.target.value }))} placeholder="Ülke"
                  style={{ border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none" }} />
                <select value={addForm.region} onChange={e => setAddForm(p => ({ ...p, region: e.target.value }))}
                  style={{ border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none" }}>
                  <option value="">Bölge seç</option>
                  {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <input type="date" value={addForm.deadline} onChange={e => setAddForm(p => ({ ...p, deadline: e.target.value }))}
                  style={{ border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none" }} />
                <input value={addForm.link} onChange={e => setAddForm(p => ({ ...p, link: e.target.value }))} placeholder="Link"
                  style={{ border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none" }} />
              </div>
              <div style={{ display: "flex", gap: 5 }}>
                <button onClick={addMaster} style={{ flex: 1, background: T.ink, color: "#fff", border: "none", borderRadius: 2, padding: "5px", fontSize: 11, cursor: "pointer" }}>Ekle</button>
                <button onClick={() => setShowAddForm(false)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 2, padding: "5px 8px", fontSize: 11, cursor: "pointer", color: T.mute }}>İptal</button>
              </div>
            </div>
          )}

          {/* Search */}
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Ara…"
            style={{ width: "100%", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 2, padding: "6px 10px", fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none", marginBottom: 6 }} />

          {/* Region filter */}
          <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <button onClick={() => setFilterRegion("")}
              style={{ ...mono, fontSize: 8, background: !filterRegion ? T.ink : "none", color: !filterRegion ? "#fff" : T.mute, border: `1px solid ${!filterRegion ? T.ink : T.border}`, borderRadius: 2, padding: "2px 6px", cursor: "pointer" }}>Tümü</button>
            {REGIONS.map(r => (
              <button key={r} onClick={() => setFilterRegion(filterRegion === r ? "" : r)}
                style={{ ...mono, fontSize: 8, background: filterRegion === r ? REGION_COLOR[r] : "none", color: filterRegion === r ? "#fff" : REGION_COLOR[r], border: `1px solid ${REGION_COLOR[r]}`, borderRadius: 2, padding: "2px 6px", cursor: "pointer" }}>
                {REGION_SHORT[r]}
              </button>
            ))}
          </div>

          {/* Tag filter */}
          <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginTop: 5 }}>
            {ALL_TAGS.filter(t => masterFests.some(m => (m.tags || "").includes(t))).map(t => (
              <button key={t} onClick={() => setFilterTag(filterTag === t ? "" : t)}
                style={{ ...mono, fontSize: 7, background: filterTag === t ? TAG_COLOR[t] : TAG_COLOR[t] + "18", color: filterTag === t ? "#fff" : TAG_COLOR[t], border: `1px solid ${TAG_COLOR[t]}`, borderRadius: 10, padding: "1px 5px", cursor: "pointer" }}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Festival list */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          <div style={{ ...mono, fontSize: 8, color: T.mute, padding: "6px 16px 3px" }}>{filtered.length} festival</div>
          {filtered.map(m => {
            const inFilm = filmFestIds.includes(m.id);
            const days = daysUntil(m.deadline);
            const isEditing = editingMaster === m.id;

            return (
              <div key={m.id}>
                <div
                  draggable={!isEditing}
                  onDragStart={() => { draggingId.current = m.id; }}
                  onDragEnd={() => { draggingId.current = null; }}
                  style={{ padding: "8px 14px", borderBottom: `1px solid ${T.borderLight}`, background: inFilm ? "#f0faf0" : "#fff", cursor: "grab", display: "flex", alignItems: "center", gap: 8 }}
                  onMouseEnter={e => { if (!inFilm) e.currentTarget.style.background = "#fafaf8"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = inFilm ? "#f0faf0" : "#fff"; }}>

                  {/* Region dot */}
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: REGION_COLOR[m.region] || "#ddd", flexShrink: 0 }} />

                  {/* Name */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: inFilm ? "#2d7a2d" : T.ink, fontWeight: inFilm ? 500 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.festival_name}
                      {m.link && <a href={m.link} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ ...mono, fontSize: 8, color: T.accent, marginLeft: 4, textDecoration: "none" }}>↗</a>}
                    </div>
                    <div style={{ display: "flex", gap: 4, marginTop: 2, flexWrap: "wrap" }}>
                      {m.country && <span style={{ ...mono, fontSize: 8, color: T.mute }}>{m.country}</span>}
                      {m.deadline && <span style={{ ...mono, fontSize: 8, color: days !== null && days >= 0 && days <= 30 ? "#c0392b" : T.mute }}>{m.deadline}{days !== null && days >= 0 && days <= 7 ? " 🔴" : days !== null && days >= 0 && days <= 30 ? " 🟡" : ""}</span>}
                      {(m.tags || "").split(",").filter(Boolean).map(t => t.trim()).slice(0, 2).map(t => (
                        <span key={t} style={{ ...mono, fontSize: 7, color: TAG_COLOR[t] || "#888", background: (TAG_COLOR[t] || "#888") + "18", borderRadius: 8, padding: "0 4px" }}>{t}</span>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
                    {inFilm
                      ? <span style={{ ...mono, fontSize: 8, color: "#2d7a2d" }}>✓</span>
                      : selProjectId && <button onClick={() => addToFilm(m.id)} title="Filme ekle"
                          style={{ ...mono, fontSize: 8, background: "none", border: `1px solid ${T.border}`, borderRadius: 2, padding: "1px 5px", cursor: "pointer", color: T.mute }}>+</button>
                    }
                    <button onClick={() => { setEditingMaster(isEditing ? null : m.id); setEditForm({ ...m }); }}
                      style={{ ...mono, fontSize: 8, background: "none", border: "none", color: isEditing ? T.accent : T.mute, cursor: "pointer", padding: 0 }}>✎</button>
                    <button onClick={() => deleteMaster(m.id)}
                      style={{ background: "none", border: "none", color: "#c0392b", fontSize: 12, cursor: "pointer", padding: 0, opacity: 0.3 }}
                      onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0.3"}>×</button>
                  </div>
                </div>

                {/* Edit form */}
                {isEditing && (
                  <div style={{ padding: "10px 14px", background: "#f8f8ff", borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      <input value={editForm.festival_name || ""} onChange={e => setEditForm(p => ({ ...p, festival_name: e.target.value }))} placeholder="Festival adı"
                        style={{ border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 8px", fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none" }} />
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                        <input value={editForm.country || ""} onChange={e => setEditForm(p => ({ ...p, country: e.target.value }))} placeholder="Ülke"
                          style={{ border: `1px solid ${T.border}`, borderRadius: 2, padding: "3px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none" }} />
                        <select value={editForm.region || ""} onChange={e => setEditForm(p => ({ ...p, region: e.target.value }))}
                          style={{ border: `1px solid ${T.border}`, borderRadius: 2, padding: "3px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none" }}>
                          <option value="">Bölge</option>
                          {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <input type="date" value={editForm.deadline || ""} onChange={e => setEditForm(p => ({ ...p, deadline: e.target.value }))}
                          style={{ border: `1px solid ${T.border}`, borderRadius: 2, padding: "3px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none" }} />
                        <input value={editForm.festival_dates || ""} onChange={e => setEditForm(p => ({ ...p, festival_dates: e.target.value }))} placeholder="Tarihler"
                          style={{ border: `1px solid ${T.border}`, borderRadius: 2, padding: "3px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none" }} />
                        <input value={editForm.link || ""} onChange={e => setEditForm(p => ({ ...p, link: e.target.value }))} placeholder="Link"
                          style={{ border: `1px solid ${T.border}`, borderRadius: 2, padding: "3px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none" }} />
                        <input value={editForm.contact || ""} onChange={e => setEditForm(p => ({ ...p, contact: e.target.value }))} placeholder="İletişim"
                          style={{ border: `1px solid ${T.border}`, borderRadius: 2, padding: "3px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none" }} />
                      </div>
                      {/* Tags */}
                      <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                        {ALL_TAGS.map(t => {
                          const active = (editForm.tags || "").split(",").map(x => x.trim()).includes(t);
                          return (
                            <span key={t} onClick={() => {
                              const cur = (editForm.tags || "").split(",").map(x => x.trim()).filter(Boolean);
                              const next = active ? cur.filter(x => x !== t) : [...cur, t];
                              setEditForm(p => ({ ...p, tags: next.join(",") }));
                            }} style={{ ...mono, fontSize: 7, background: active ? TAG_COLOR[t] : TAG_COLOR[t] + "18", color: active ? "#fff" : TAG_COLOR[t], border: `1px solid ${TAG_COLOR[t]}`, borderRadius: 8, padding: "1px 5px", cursor: "pointer" }}>
                              {t}
                            </span>
                          );
                        })}
                      </div>
                      <div style={{ display: "flex", gap: 5 }}>
                        <button onClick={saveMasterEdit} style={{ flex: 1, background: T.ink, color: "#fff", border: "none", borderRadius: 2, padding: "5px", fontSize: 11, cursor: "pointer" }}>Kaydet</button>
                        <button onClick={() => setEditingMaster(null)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 2, padding: "5px 8px", fontSize: 11, cursor: "pointer", color: T.mute }}>İptal</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>}

      {/* ── RIGHT: Film Strategy ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Film landing screen — proje seçilmemişse */}
        {!selProjectId ? (
          <div style={{ flex: 1, overflowY: "auto", padding: "48px 40px" }}>
            <div style={{ maxWidth: 900, margin: "0 auto" }}>

              {/* Poster grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 24, marginBottom: 32 }}>
                {activeProjects.map(p => {
                  const links = filmLinks.filter(l => l.project_id === p.id);
                  const applied = links.filter(l => ["Applied","Contacted","Selected"].includes(l.status)).length;
                  const targeted = links.filter(l => l.status === "Hedefleniyor").length;
                  const selected = links.filter(l => l.status === "Selected").length;
                  return (
                    <div key={p.id} style={{ cursor: "pointer", borderRadius: 4, overflow: "hidden", border: `1px solid ${T.border}`, background: "#fff", position: "relative" }}
                      onClick={() => setSelProjectId(p.id)}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.10)"}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                      {/* Poster */}
                      <div style={{ height: 240, background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                        {p.poster_url
                          ? <img src={p.poster_url} alt={p.title} style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
                          : <span style={{ ...mono, fontSize: 22, color: T.border, fontWeight: 700 }}>{p.title.charAt(0)}</span>
                        }
                      </div>
                      {/* Remove button */}
                      <button onClick={e => { e.stopPropagation(); removeActiveProject(p.id); }}
                        style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.35)", border: "none", color: "#fff", borderRadius: "50%", width: 22, height: 22, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>×</button>
                      {/* Info */}
                      <div style={{ padding: "10px 12px 12px" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.ink, marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                        {/* Stats */}
                        <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                          {selected > 0 && (
                            <span style={{ ...mono, fontSize: 9, color: "#2d7a2d", fontWeight: 700 }}>{selected} seçilen</span>
                          )}
                          <span style={{ ...mono, fontSize: 9, color: "#4a6fa5" }}>{applied} başvurulan</span>
                        </div>
                        {/* Festival name list — applied/selected only */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {links
                            .filter(l => ["Applied","Contacted","Selected"].includes(l.status))
                            .sort((a,b) => {
                              const order = {"Selected":0,"Applied":1,"Contacted":2};
                              return (order[a.status]??9) - (order[b.status]??9);
                            })
                            .map(l => {
                              const m = masterFests.find(x => x.id === l.master_id);
                              if (!m) return null;
                              const isSelected = l.status === "Selected";
                              return (
                                <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  <span style={{ width: 4, height: 4, borderRadius: "50%", background: isSelected ? "#2d7a2d" : "#4a6fa5", flexShrink: 0 }}/>
                                  <span style={{ fontSize: 10, color: isSelected ? "#2d7a2d" : T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.festival_name}</span>
                                </div>
                              );
                            })
                          }
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Film ekle — sadece fol */}
              {accessLevel==="full" && <AddProjectRow projects={projects} activeProjectIds={activeProjectIds} onAdd={(p) => {
                setActiveProjectIds(prev => {
                  const next = [...prev, p.id];
                  localStorage.setItem("festival_active_projects", JSON.stringify(next));
                  return next;
                });
                setSelProjectId(p.id);
              }}/>}
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Film header with back button */}
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={async e => {
                e.preventDefault(); setDragOver(false);
                if (draggingId.current) await addToFilm(draggingId.current);
              }}
              style={{ padding: "10px 20px", background: dragOver ? "#f0ece6" : T.bg, borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", transition: "background .1s", flexShrink: 0, border: dragOver ? `2px dashed ${T.accent}` : `none`, borderBottom: `1px solid ${T.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button onClick={() => setSelProjectId(null)} style={{ ...mono, fontSize: 9, background: "none", border: `1px solid ${T.border}`, borderRadius: 2, padding: "3px 8px", cursor: "pointer", color: T.mute }}>← Filmler</button>
                <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}>{selProject?.title}</span>
                <span style={{ ...mono, fontSize: 9, color: T.mute }}>{filmFestLinks.length} festival</span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{...mono,fontSize:9,color:dragOver?T.accent:T.mute}}>{dragOver?"Bırak!":"← Sürükle veya + ekle"}</span>
                {/* Region filter */}
                {(() => {
                  const regions = [...new Set(
                    filmLinks.filter(l=>l.project_id===selProjectId)
                      .map(l=>masterFests.find(x=>x.id===l.master_id)?.region)
                      .filter(Boolean)
                  )].sort();
                  if (!regions.length) return null;
                  return (
                    <div style={{display:"flex",border:`1px solid ${T.border}`,borderRadius:2,overflow:"hidden"}}>
                      <button onClick={()=>setFilterFilmRegion("")}
                        style={{...mono,fontSize:8,background:filterFilmRegion===""?T.ink:"none",color:filterFilmRegion===""?"#fff":T.mute,border:"none",padding:"3px 8px",cursor:"pointer"}}>
                        Tümü
                      </button>
                      {regions.map(r=>(
                        <button key={r} onClick={()=>setFilterFilmRegion(r===filterFilmRegion?"":r)}
                          style={{...mono,fontSize:8,background:filterFilmRegion===r?T.ink:"none",color:filterFilmRegion===r?"#fff":T.mute,border:"none",padding:"3px 8px",cursor:"pointer",borderLeft:`1px solid ${T.border}`}}>
                          {r}
                        </button>
                      ))}
                    </div>
                  );
                })()}
                <div style={{display:"flex",border:`1px solid ${T.border}`,borderRadius:2,overflow:"hidden"}}>
                  {["all","2026","2027"].map(y=>(
                    <button key={y} onClick={()=>setFilterYear(y)}
                      style={{...mono,fontSize:8,background:filterYear===y?T.ink:"none",color:filterYear===y?"#fff":T.mute,border:"none",padding:"3px 8px",cursor:"pointer"}}>
                      {y==="all"?"Tümü":y}
                    </button>
                  ))}
                </div>
                <div style={{display:"flex",border:`1px solid ${T.border}`,borderRadius:2,overflow:"hidden"}}>
                  <button onClick={()=>setViewMode("list")} style={{...mono,fontSize:8,background:viewMode==="list"?T.ink:"none",color:viewMode==="list"?"#fff":T.mute,border:"none",padding:"3px 8px",cursor:"pointer"}}>Liste</button>
                  <button onClick={()=>setViewMode("kanban")} style={{...mono,fontSize:8,background:viewMode==="kanban"?T.ink:"none",color:viewMode==="kanban"?"#fff":T.mute,border:"none",padding:"3px 8px",cursor:"pointer"}}>Bölge</button>
                </div>
              </div>
            </div>

            {/* Film's festival list — Column View */}
            {viewMode==="kanban"?(
              <FestivalKanban filmFestLinks={filmFestLinks} masterFests={masterFests} updateStatus={updateStatus} removeFromFilm={removeFromFilm} festivalSortKey={festivalSortKey} festivalMonthLabel={festivalMonthLabel}/>
            ):(
              <FestivalColumns filmFestLinks={filmFestLinks} masterFests={masterFests} updateStatus={updateStatus} removeFromFilm={removeFromFilm} festivalSortKey={festivalSortKey} festivalMonthLabel={festivalMonthLabel} updateNote={updateNote} updateEmail={updateEmail} updateLabels={updateLabels} draggingLinkId={draggingLinkId} dragOver2027={dragOver2027} setDragOver2027={setDragOver2027} onAddTask={addFestivalTask}/>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Email Field ── */
function EmailField({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(()=>setVal(value),[value]);
  const commit = () => { setEditing(false); if(val!==value) onSave(val); };
  if (editing) return (
    <input value={val} onChange={e=>setVal(e.target.value)}
      onBlur={commit} onKeyDown={e=>{if(e.key==="Enter")commit();if(e.key==="Escape"){setVal(value);setEditing(false);}}}
      autoFocus placeholder="mail@festival.com"
      style={{flex:"0 0 140px",fontSize:11,border:"1px solid #4a6fa5",borderRadius:2,padding:"1px 6px",outline:"none",fontFamily:"'JetBrains Mono',monospace",color:"#444",minWidth:80,maxWidth:160}}/>
  );
  return (
    <span onDoubleClick={()=>setEditing(true)} title="Mail için çift tıkla"
      style={{flex:"0 0 140px",fontSize:10,color:value?"#4a6fa5":"#ddd",cursor:"text",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:160,fontFamily:"'JetBrains Mono',monospace"}}>
      {value
        ? <a href={`mailto:${value}`} onClick={e=>e.stopPropagation()} style={{color:"#4a6fa5",textDecoration:"none"}}>{value}</a>
        : <span style={{fontStyle:"italic",color:"#ccc"}}>✉ mail…</span>}
    </span>
  );
}

/* ── Label Field ── */
const LABEL_PRESETS = ["Filmarket","Cinando","Hot Docs","Doc Nomads","WeAreDoc","Online","Submission Fee","No Fee"];
const LABEL_COLOR = "#c9843a";

function LabelField({ labels, onSave }) {
  const [adding, setAdding] = useState(false);
  const [custom, setCustom] = useState("");

  const addLabel = (l) => {
    const trimmed = l.trim();
    if (!trimmed || labels.includes(trimmed)) { setAdding(false); setCustom(""); return; }
    onSave([...labels, trimmed]);
    setAdding(false);
    setCustom("");
  };

  const removeLabel = (l) => onSave(labels.filter(x => x !== l));

  return (
    <div style={{display:"flex",alignItems:"center",gap:3,flexWrap:"nowrap",flexShrink:0,maxWidth:220,overflow:"hidden"}}>
      {labels.map(l => (
        <span key={l} style={{display:"inline-flex",alignItems:"center",gap:2,background:"#fdf3e3",border:`1px solid ${LABEL_COLOR}`,borderRadius:10,padding:"1px 6px",fontSize:9,color:LABEL_COLOR,whiteSpace:"nowrap",flexShrink:0}}>
          {l}
          <span onClick={()=>removeLabel(l)} style={{cursor:"pointer",color:LABEL_COLOR,fontSize:11,lineHeight:1,marginLeft:1,opacity:0.6}} onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.6"}>×</span>
        </span>
      ))}
      {adding ? (
        <div style={{position:"relative",display:"inline-block"}}>
          <input value={custom} onChange={e=>setCustom(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter")addLabel(custom);if(e.key==="Escape"){setAdding(false);setCustom("");}}}
            autoFocus placeholder="label…"
            style={{width:80,fontSize:10,border:"1px solid #c9843a",borderRadius:10,padding:"1px 6px",outline:"none",fontFamily:"'Inter',sans-serif",color:"#444"}}/>
          {/* Presets dropdown */}
          <div style={{position:"absolute",top:"100%",left:0,zIndex:100,background:"#fff",border:"1px solid #eee",borderRadius:4,boxShadow:"0 2px 8px rgba(0,0,0,0.12)",minWidth:130,marginTop:2}}>
            {LABEL_PRESETS.filter(p=>!labels.includes(p)&&(!custom||p.toLowerCase().includes(custom.toLowerCase()))).map(p=>(
              <div key={p} onMouseDown={e=>{e.preventDefault();addLabel(p);}}
                style={{padding:"4px 10px",fontSize:10,cursor:"pointer",color:"#555",whiteSpace:"nowrap"}}
                onMouseEnter={e=>e.currentTarget.style.background="#f5f0e8"} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                {p}
              </div>
            ))}
            {custom.trim() && !labels.includes(custom.trim()) && !LABEL_PRESETS.includes(custom.trim()) && (
              <div onMouseDown={e=>{e.preventDefault();addLabel(custom);}}
                style={{padding:"4px 10px",fontSize:10,cursor:"pointer",color:"#c9843a",borderTop:"1px solid #eee",whiteSpace:"nowrap"}}
                onMouseEnter={e=>e.currentTarget.style.background="#fdf3e3"} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                + "{custom.trim()}" ekle
              </div>
            )}
          </div>
        </div>
      ) : (
        <span onClick={()=>setAdding(true)} title="Label ekle"
          style={{fontSize:10,color:"#ddd",cursor:"pointer",flexShrink:0,userSelect:"none",padding:"1px 4px",borderRadius:10,border:"1px dashed #ddd"}}
          onMouseEnter={e=>{e.currentTarget.style.color="#c9843a";e.currentTarget.style.borderColor="#c9843a";}} 
          onMouseLeave={e=>{e.currentTarget.style.color="#ddd";e.currentTarget.style.borderColor="#ddd";}}>
          + label
        </span>
      )}
    </div>
  );
}

/* ── Festival Columns — Status Columns ── */
function FestivalColumns({ filmFestLinks, masterFests, updateStatus, removeFromFilm, festivalSortKey, festivalMonthLabel, updateNote, updateEmail, updateLabels, draggingLinkId, dragOver2027, setDragOver2027, onAddTask }) {

  const COL_DEFS = [
    { id:"applied",   label:"Başvurulanlar",   statuses:["Applied","Contacted"], color:"#4a6fa5", defaultStatus:"Applied",      defaultVisible:false },
    { id:"targeted",  label:"Başvurulacaklar",  statuses:["Hedefleniyor"],        color:"#888",    defaultStatus:"Hedefleniyor", defaultVisible:true  },
    { id:"ada",       label:"Ada",              statuses:["Ada"],                 color:"#e17055", defaultStatus:"Ada",          defaultVisible:true  },
    { id:"accepted",  label:"Kabul Edilenler",  statuses:["Selected"],            color:"#2d7a2d", defaultStatus:"Selected",     defaultVisible:false },
    { id:"rejected",  label:"Reddedilenler",    statuses:["Rejected","Not Applied"], color:"#c0392b", defaultStatus:"Rejected",  defaultVisible:false },
  ];

  const [visibleCols, setVisibleCols] = useState(() => COL_DEFS.filter(c=>c.defaultVisible).map(c=>c.id));
  const [dragOverCol, setDragOverCol] = useState(null); // col id being hovered

  const toggleCol = (id) => setVisibleCols(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);

  const sortedLinks = (links) => [...links].sort((a,b) => {
    const ma = masterFests.find(x=>x.id===a.master_id);
    const mb = masterFests.find(x=>x.id===b.master_id);
    return (ma?festivalSortKey(ma):"9999").localeCompare(mb?festivalSortKey(mb):"9999");
  });

  const links2027 = filmFestLinks.filter(l => l.status === "2027");
  const activeLinks = filmFestLinks.filter(l => l.status !== "2027");
  const shownCols = COL_DEFS.filter(c => visibleCols.includes(c.id));

  function FestRow({ link, wide, onAddTask }) {
    const m = masterFests.find(x=>x.id===link.master_id);
    if (!m) return null;
    const days = daysUntil(m.deadline);
    const monthLabel = festivalMonthLabel(m);
    const statusColor = STATUS_COLOR[link.status || "Hedefleniyor"] || "#888";

    if (wide) {
      // Ada column: card layout with full-width textarea note
      return (
        <div
          draggable
          onDragStart={e=>{ draggingLinkId.current=link.id; e.dataTransfer.effectAllowed="move"; }}
          onDragEnd={()=>{ draggingLinkId.current=null; setDragOverCol(null); }}
          style={{padding:"10px 12px",borderBottom:`1px solid ${T.borderLight}`,background:"#fff",cursor:"grab",userSelect:"none"}}
          onMouseEnter={e=>e.currentTarget.style.background="#fef8f6"}
          onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
          {/* Top row: name + meta + status + remove */}
          <div style={{display:"flex",alignItems:"flex-start",gap:8,marginBottom:6}}>
            {monthLabel && <span style={{...mono,fontSize:7,color:"#fff",background:"#4a6fa5",borderRadius:2,padding:"1px 4px",flexShrink:0,marginTop:2}}>{monthLabel}</span>}
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,fontWeight:600,color:T.ink,lineHeight:1.3}}>
                {m.festival_name}
                {m.link&&<a href={m.link} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{...mono,fontSize:8,color:T.accent,marginLeft:4,textDecoration:"none"}}>↗</a>}
              </div>
              <div style={{display:"flex",gap:6,marginTop:2,flexWrap:"wrap"}}>
                {m.country&&<span style={{...mono,fontSize:8,color:T.mute}}>{m.country}</span>}
                {m.deadline&&<span style={{...mono,fontSize:8,color:days!==null&&days>=0&&days<=30?"#c0392b":T.mute}}>{m.deadline}{days!==null&&days>=0&&days<=7?" 🔴":""}</span>}
                {m.festival_dates&&<span style={{...mono,fontSize:8,color:"#c0392b"}}>{m.festival_dates}</span>}
              </div>
            </div>
            <select value={link.status||"Hedefleniyor"} onChange={e=>updateStatus(link.id,e.target.value)}
              style={{...mono,fontSize:8,background:STATUS_BG[link.status]||"#fff",color:statusColor,border:`1px solid ${statusColor}`,borderRadius:10,padding:"2px 6px",cursor:"pointer",fontWeight:600,flexShrink:0}}>
              {STATUSES.map(s=><option key={s}>{s}</option>)}
            </select>
            <button onClick={()=>removeFromFilm(m.id)}
              style={{background:"none",border:"none",color:"#c0392b",fontSize:13,cursor:"pointer",padding:0,opacity:0.3,flexShrink:0}}
              onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.3"}>×</button>
          </div>
          {/* Full-width note textarea */}
          <AdaNoteInput value={link.film_specific_notes||""} onSave={v=>updateNote(link.id,v)}/>
          {/* Görev ekle */}
          {onAddTask && (
            <div style={{marginTop:6,display:"flex",gap:4,justifyContent:"flex-end"}}>
              <span style={{...mono,fontSize:8,color:"#aaa",alignSelf:"center",marginRight:2}}>+ görev →</span>
              <button onClick={()=>onAddTask(m.festival_name, link.film_specific_notes||"", "ADA")}
                style={{...mono,fontSize:8,background:"none",border:"1px solid #9b7ec8",color:"#9b7ec8",borderRadius:3,padding:"3px 10px",cursor:"pointer",fontWeight:600}}
                onMouseEnter={e=>{e.currentTarget.style.background="#9b7ec8";e.currentTarget.style.color="#fff";}}
                onMouseLeave={e=>{e.currentTarget.style.background="none";e.currentTarget.style.color="#9b7ec8";}}>
                ADA
              </button>
              <button onClick={()=>onAddTask(m.festival_name, link.film_specific_notes||"", "CEVIK")}
                style={{...mono,fontSize:8,background:"none",border:"1px solid #4a6fa5",color:"#4a6fa5",borderRadius:3,padding:"3px 10px",cursor:"pointer",fontWeight:600}}
                onMouseEnter={e=>{e.currentTarget.style.background="#4a6fa5";e.currentTarget.style.color="#fff";}}
                onMouseLeave={e=>{e.currentTarget.style.background="none";e.currentTarget.style.color="#4a6fa5";}}>
                CEVİK
              </button>
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        draggable
        onDragStart={e=>{ draggingLinkId.current=link.id; e.dataTransfer.effectAllowed="move"; }}
        onDragEnd={()=>{ draggingLinkId.current=null; setDragOverCol(null); }}
        style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderBottom:`1px solid ${T.borderLight}`,background:"#fff",cursor:"grab",userSelect:"none"}}
        onMouseEnter={e=>e.currentTarget.style.background="#f8f4ef"}
        onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
        {monthLabel && <span style={{...mono,fontSize:7,color:"#fff",background:"#4a6fa5",borderRadius:2,padding:"1px 4px",flexShrink:0}}>{monthLabel}</span>}
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:11,fontWeight:500,color:T.ink,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            {m.festival_name}
            {m.link&&<a href={m.link} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{...mono,fontSize:8,color:T.accent,marginLeft:4,textDecoration:"none"}}>↗</a>}
          </div>
          <div style={{display:"flex",gap:4,marginTop:1}}>
            {m.country&&<span style={{...mono,fontSize:8,color:T.mute}}>{m.country}</span>}
            {m.deadline&&<span style={{...mono,fontSize:8,color:days!==null&&days>=0&&days<=30?"#c0392b":T.mute}}>{m.deadline}{days!==null&&days>=0&&days<=7?" 🔴":""}</span>}
          </div>
        </div>
        {m.festival_dates&&<span style={{...mono,fontSize:8,color:"#c0392b",whiteSpace:"nowrap",flexShrink:0}}>{m.festival_dates}</span>}
        <NoteInput value={link.film_specific_notes||""} onSave={v=>updateNote(link.id,v)}/>
        <select value={link.status||"Hedefleniyor"} onChange={e=>updateStatus(link.id,e.target.value)}
          style={{...mono,fontSize:8,background:STATUS_BG[link.status]||"#fff",color:statusColor,border:`1px solid ${statusColor}`,borderRadius:10,padding:"2px 6px",cursor:"pointer",fontWeight:600,flexShrink:0}}>
          {STATUSES.map(s=><option key={s}>{s}</option>)}
        </select>
        <button onClick={()=>removeFromFilm(m.id)}
          style={{background:"none",border:"none",color:"#c0392b",fontSize:13,cursor:"pointer",padding:0,opacity:0.3,flexShrink:0}}
          onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.3"}>×</button>
      </div>
    );
  }

  if (filmFestLinks.length === 0) return (
    <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:40}}>
      <div style={{...mono,fontSize:10,color:T.mute,textAlign:"center"}}>Sol listeden festival sürükle veya + butonuna bas.</div>
    </div>
  );

  return (
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {/* Column toggle bar */}
      <div style={{display:"flex",alignItems:"center",gap:6,padding:"8px 16px",borderBottom:`1px solid ${T.border}`,background:T.bg,flexShrink:0}}>
        <span style={{...mono,fontSize:8,color:T.mute,marginRight:4,letterSpacing:"0.08em"}}>SÜTUNLAR</span>
        {COL_DEFS.map(col => (
          <button key={col.id} onClick={()=>toggleCol(col.id)} style={{
            ...mono, fontSize:8, cursor:"pointer", borderRadius:10, padding:"2px 10px",
            background: visibleCols.includes(col.id) ? col.color : "transparent",
            color: visibleCols.includes(col.id) ? "#fff" : T.mute,
            border: `1px solid ${visibleCols.includes(col.id) ? col.color : T.border}`,
            fontWeight:600, transition:"all 0.12s"
          }}>
            {col.label}
            <span style={{opacity:0.7,marginLeft:4}}>
              {activeLinks.filter(l=>col.statuses.includes(l.status||"Hedefleniyor")).length}
            </span>
          </button>
        ))}
      </div>

      {/* Columns */}
      <div style={{flex:1,display:"grid",gridTemplateColumns:`repeat(${shownCols.length},1fr)`,gap:0,overflow:"hidden"}}>
        {shownCols.map((col, i) => {
          const colLinks = sortedLinks(activeLinks.filter(l=>col.statuses.includes(l.status||"Hedefleniyor")));
          const isOver = dragOverCol === col.id;
          return (
            <div key={col.id}
              onDragOver={e=>{ e.preventDefault(); e.dataTransfer.dropEffect="move"; setDragOverCol(col.id); }}
              onDragLeave={e=>{ if(!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
              onDrop={async e=>{
                e.preventDefault();
                setDragOverCol(null);
                if(!draggingLinkId.current) return;
                // Drop onto this column → set to column's default status
                await updateStatus(draggingLinkId.current, col.defaultStatus);
                draggingLinkId.current=null;
              }}
              style={{display:"flex",flexDirection:"column",overflow:"hidden",borderLeft:i>0?`1px solid ${T.border}`:"none",transition:"background 0.1s",background:isOver?col.color+"0d":"transparent"}}>
              {/* Column header */}
              <div style={{padding:"10px 14px 8px",borderBottom:`2px solid ${isOver?col.color:col.color+"88"}`,background:isOver?col.color+"12":"#fff",flexShrink:0,transition:"all 0.1s"}}>
                <div style={{fontSize:12,fontWeight:600,color:col.color}}>{col.label}</div>
                <div style={{...mono,fontSize:9,color:isOver?col.color:T.mute,marginTop:1}}>
                  {isOver ? "Bırak!" : `${colLinks.length} festival`}
                </div>
              </div>
              {/* Rows */}
              <div style={{flex:1,overflowY:"auto"}}>
                {colLinks.length===0
                  ? <div style={{...mono,fontSize:9,color:isOver?col.color:T.border,padding:"20px 14px",textAlign:"center"}}>{isOver?"↓ Buraya bırak":"—"}</div>
                  : colLinks.map(link=><FestRow key={link.id} link={link} wide={col.id==="ada"} onAddTask={col.id==="ada"?onAddTask:null}/>)
                }
              </div>
            </div>
          );
        })}
      </div>

      {/* 2027 drop zone */}
      {filmFestLinks.length > 0 && (
        <div
          onDragOver={e=>{e.preventDefault();setDragOver2027(true);setDragOverCol(null);}}
          onDragLeave={()=>setDragOver2027(false)}
          onDrop={async e=>{
            e.preventDefault();setDragOver2027(false);
            if(!draggingLinkId.current) return;
            await updateStatus(draggingLinkId.current,"2027");
            draggingLinkId.current=null;
          }}
          style={{margin:"8px 16px",padding:"10px",border:`2px dashed ${dragOver2027?"#9b7ec8":T.border}`,borderRadius:4,background:dragOver2027?"#f0eeff":T.bg,textAlign:"center",transition:"all .1s",cursor:"default",flexShrink:0}}>
          <span style={{...mono,fontSize:9,color:dragOver2027?"#9b7ec8":T.mute}}>
            {dragOver2027?"Bırak → 2027":`🗓 2027 — ${links2027.length > 0 ? links2027.length+" festival planlandı" : "Seneye planlanacak festivalleri buraya sürükle"}`}
          </span>
        </div>
      )}
    </div>
  );
}

/* ── Festival Kanban — Region Columns ── */
function FestivalKanban({ filmFestLinks, masterFests, updateStatus, removeFromFilm, festivalSortKey, festivalMonthLabel }) {
  const COLS = ["Europe","North Am","Asia-Caucasus-Africa","Latam","Oceania","(Belirsiz)"];
  const COL_COLOR = {"Europe":"#4a6fa5","North Am":"#c0392b","Asia-Caucasus-Africa":"#c9843a","Latam":"#3a8a7c","Oceania":"#9b7ec8","(Belirsiz)":"#aaa"};
  const COL_SHORT = {"Europe":"Europe","North Am":"North Am","Asia-Caucasus-Africa":"Asia · Caucasus · Africa","Latam":"Latin America","Oceania":"Oceania","(Belirsiz)":"Belirsiz"};
  const STATUS_DOT = {"Hedefleniyor":"#888","Applied":"#4a6fa5","Selected":"#2d7a2d","Rejected":"#c0392b","Contacted":"#c9843a","Not Applied":"#ccc","2027":"#9b7ec8"};

  function getCol(m) {
    if (!m) return "(Belirsiz)";
    return COLS.includes(m.region) ? m.region : "(Belirsiz)";
  }

  // Separate 2027 from current
  const currentLinks = filmFestLinks.filter(l => l.status !== "2027");
  const links2027 = filmFestLinks.filter(l => l.status === "2027");

  // Group current by region
  const byRegion = {};
  COLS.forEach(c => { byRegion[c] = []; });
  currentLinks.forEach(link => {
    const m = masterFests.find(x => x.id === link.master_id);
    const col = getCol(m);
    byRegion[col].push({ link, master: m });
  });
  COLS.forEach(col => {
    byRegion[col].sort((a, b) => (a.master?festivalSortKey(a.master):"9999").localeCompare(b.master?festivalSortKey(b.master):"9999"));
  });

  // Group 2027 by region
  const byRegion2027 = {};
  COLS.forEach(c => { byRegion2027[c] = []; });
  links2027.forEach(link => {
    const m = masterFests.find(x => x.id === link.master_id);
    const col = getCol(m);
    byRegion2027[col].push({ link, master: m });
  });
  COLS.forEach(col => {
    byRegion2027[col].sort((a, b) => (a.master?festivalSortKey(a.master):"9999").localeCompare(b.master?festivalSortKey(b.master):"9999"));
  });

  const nonEmptyCols = COLS.filter(c => byRegion[c].length > 0);
  const nonEmpty2027 = COLS.filter(c => byRegion2027[c].length > 0);

  function FestCard({ link, m }) {
    if (!m) return null;
    const monthLabel = festivalMonthLabel(m);
    const statusColor = STATUS_DOT[link.status || "Hedefleniyor"] || "#888";
    return (
      <div style={{background:"#fff",border:`1px solid ${T.borderLight}`,borderLeft:`3px solid ${statusColor}`,borderRadius:3,padding:"7px 8px",marginBottom:5}}
        onMouseEnter={e=>e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,0.08)"}
        onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
        <div style={{display:"flex",alignItems:"flex-start",gap:5,marginBottom:3}}>
          {monthLabel&&<span style={{...mono,fontSize:7,color:"#fff",background:"#4a6fa5",borderRadius:2,padding:"1px 4px",flexShrink:0,marginTop:1}}>{monthLabel}</span>}
          <span style={{fontSize:11,fontWeight:500,color:T.ink,lineHeight:1.3}}>{m.festival_name}</span>
          {m.link&&<a href={m.link} target="_blank" rel="noreferrer" style={{...mono,fontSize:8,color:T.accent,textDecoration:"none",flexShrink:0}}>↗</a>}
        </div>
        {m.festival_dates&&<div style={{...mono,fontSize:8,color:T.mute,marginBottom:4}}>{m.festival_dates}</div>}
        <div style={{display:"flex",alignItems:"center",gap:4}}>
          <select value={link.status||"Hedefleniyor"} onChange={e=>updateStatus(link.id,e.target.value)}
            style={{...mono,fontSize:8,flex:1,background:"none",border:`1px solid ${statusColor}`,borderRadius:8,padding:"1px 4px",cursor:"pointer",color:statusColor,fontWeight:600}}>
            {["Hedefleniyor","Applied","Selected","Rejected","Contacted","Not Applied","2027"].map(s=><option key={s}>{s}</option>)}
          </select>
          <button onClick={()=>removeFromFilm(m.id)}
            style={{background:"none",border:"none",color:"#c0392b",fontSize:12,cursor:"pointer",padding:0,opacity:0.3,flexShrink:0}}
            onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.3"}>×</button>
        </div>
      </div>
    );
  }

  if (filmFestLinks.length === 0) return (
    <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:40}}>
      <div style={{...mono,fontSize:10,color:T.mute,textAlign:"center"}}>Henüz festival eklenmemiş.</div>
    </div>
  );

  return (
    <div style={{flex:1,overflowY:"auto",padding:"12px 16px"}}>
      {/* Current year grid */}
      {nonEmptyCols.length > 0 && (
        <div style={{marginBottom:24}}>
          <div style={{...mono,fontSize:9,color:T.mute,marginBottom:10,letterSpacing:"0.1em"}}>
            {new Date().getFullYear()} · {currentLinks.length} FESTİVAL
          </div>
          <div style={{display:"grid",gridTemplateColumns:`repeat(${nonEmptyCols.length},1fr)`,gap:10,alignItems:"start"}}>
            {nonEmptyCols.map(col => (
              <div key={col} style={{background:T.bg,border:`1px solid ${T.border}`,borderTop:`3px solid ${COL_COLOR[col]}`,borderRadius:4,overflow:"hidden"}}>
                <div style={{padding:"8px 10px",borderBottom:`1px solid ${T.border}`,background:"#fff"}}>
                  <div style={{fontSize:11,fontWeight:600,color:COL_COLOR[col]}}>{COL_SHORT[col]}</div>
                  <div style={{...mono,fontSize:8,color:T.mute,marginTop:1}}>{byRegion[col].length} festival</div>
                </div>
                <div style={{padding:"6px"}}>
                  {byRegion[col].map(({link,master:m}) => <FestCard key={link.id} link={link} m={m}/>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 2027 grid */}
      {nonEmpty2027.length > 0 && (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
            <div style={{flex:1,height:1,background:T.border}}/>
            <div style={{...mono,fontSize:9,color:"#9b7ec8",fontWeight:600,letterSpacing:"0.1em"}}>2027 · {links2027.length} FESTİVAL</div>
            <div style={{flex:1,height:1,background:T.border}}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:`repeat(${nonEmpty2027.length},1fr)`,gap:10,alignItems:"start"}}>
            {nonEmpty2027.map(col => (
              <div key={col} style={{background:"#f8f4ff",border:`1px solid #d4c8f0`,borderTop:`3px solid ${COL_COLOR[col]}`,borderRadius:4,overflow:"hidden"}}>
                <div style={{padding:"8px 10px",borderBottom:"1px solid #d4c8f0",background:"#fff"}}>
                  <div style={{fontSize:11,fontWeight:600,color:COL_COLOR[col]}}>{COL_SHORT[col]}</div>
                  <div style={{...mono,fontSize:8,color:T.mute,marginTop:1}}>{byRegion2027[col].length} festival</div>
                </div>
                <div style={{padding:"6px"}}>
                  {byRegion2027[col].map(({link,master:m}) => <FestCard key={link.id} link={link} m={m}/>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
